import { Button, Icons } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, type ReactNode, useCallback, useRef, useState } from "react";
import { customerBackendApi } from "../features/api/backend.ts";
import { type MessageKey, useT } from "../features/i18n/index.ts";
import {
  postTypeLabel,
  type ScreenPost,
  type ScreenPostStatus,
  type ScreenPostType,
  screenPostStatusMeta,
  validateScreenPost,
} from "../features/screen/screen.ts";
import { useSession } from "../features/session/session-context.tsx";
import { formatMoney } from "../lib/format.ts";

export const Route = createFileRoute("/screen")({
  component: ScreenPage,
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MIN_TIP_SATANG = 10_000;

const POST_TYPES: { type: ScreenPostType; emoji: string; descKey: MessageKey }[] = [
  { type: "message", emoji: "💬", descKey: "screen.type.messageDesc" },
  { type: "warp", emoji: "📸", descKey: "screen.type.warpDesc" },
  { type: "song_request", emoji: "🎵", descKey: "screen.type.songDesc" },
  { type: "tip", emoji: "💸", descKey: "screen.type.tipDesc" },
];

// ---------------------------------------------------------------------------
// Top-level page (picks checked-in session, shows not-checked-in guard)
// ---------------------------------------------------------------------------

function ScreenPage() {
  const t = useT();
  const { checkIn } = useSession();
  const navigate = useNavigate();

  if (!checkIn) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-5 px-[18px] py-8">
        <span className="text-5xl">📺</span>
        <h1 className="h-display text-center text-[1.5rem]">{t("screen.guardTitle")}</h1>
        <p className="text-center text-cream-3 text-sm">{t("screen.checkInFirst")}</p>
        <Button className="w-full" onClick={() => navigate({ to: "/home" })}>
          {t("common.backHome")}
        </Button>
      </div>
    );
  }

  return <ScreenFlow venueSessionId={checkIn.venueSessionId} />;
}

// ---------------------------------------------------------------------------
// Main flow once the user is checked in
// ---------------------------------------------------------------------------

type FlowPhase = "pick-type" | "compose" | "success";

interface ScreenFlowProps {
  venueSessionId: string;
}

function ScreenFlow({ venueSessionId }: ScreenFlowProps) {
  const t = useT();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<FlowPhase>("pick-type");
  const [selectedType, setSelectedType] = useState<ScreenPostType>("message");
  const [lastPost, setLastPost] = useState<ScreenPost | null>(null);

  if (phase === "success" && lastPost) {
    return (
      <SuccessView
        post={lastPost}
        onBack={() => setPhase("pick-type")}
        onHome={() => navigate({ to: "/home" })}
        venueSessionId={venueSessionId}
      />
    );
  }

  if (phase === "compose") {
    return (
      <ComposeView
        postType={selectedType}
        venueSessionId={venueSessionId}
        onBack={() => setPhase("pick-type")}
        onSuccess={(post) => {
          setLastPost(post);
          setPhase("success");
        }}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/home" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="jod-kicker">{t("screen.kicker")}</div>
      <h1 className="h-display mt-1 text-[1.7rem]">{t("screen.titleBig")}</h1>
      <p className="mt-1 text-cream-3 text-sm">{t("screen.pickHint")}</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {POST_TYPES.map(({ type, emoji, descKey }) => {
          const on = selectedType === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              aria-pressed={on}
              className={`flex flex-col items-start gap-2 rounded-xl border px-4 py-4 text-left transition-all ${
                on
                  ? "border-[oklch(79%_0.12_80_/_50%)] bg-[oklch(79%_0.12_80_/_12%)]"
                  : "border-hairline bg-[oklch(100%_0_0_/_3%)]"
              }`}
            >
              <span className="text-2xl">{emoji}</span>
              <span
                className={`font-display font-semibold text-sm ${on ? "text-gold" : "text-cream"}`}
              >
                {postTypeLabel(type)}
              </span>
              <span className="text-cream-3 text-[0.7rem]">{t(descKey)}</span>
            </button>
          );
        })}
      </div>

      <Button
        size="lg"
        variant="accent"
        className="mt-6 w-full"
        onClick={() => setPhase("compose")}
      >
        {t("screen.next")}
      </Button>

      <MyPostsList venueSessionId={venueSessionId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compose view — per-type form
// ---------------------------------------------------------------------------

interface ComposeViewProps {
  postType: ScreenPostType;
  venueSessionId: string;
  onBack: () => void;
  onSuccess: (post: ScreenPost) => void;
}

function ComposeView({ postType, venueSessionId, onBack, onSuccess }: ComposeViewProps) {
  const t = useT();
  const { toast } = useSession();

  // Form fields
  const [message, setMessage] = useState("");
  const [igHandle, setIgHandle] = useState("");
  const [songTitle, setSongTitle] = useState("");
  const [tipTargetId, setTipTargetId] = useState("");
  const [tipAmountSatang, setTipAmountSatang] = useState(DEFAULT_MIN_TIP_SATANG);
  const [showAvatar, setShowAvatar] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [photoKey, setPhotoKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const backendEnabled = customerBackendApi !== null;

  const tipTargetsQuery = useQuery({
    queryKey: ["screen", "tip-targets", venueSessionId],
    queryFn: () =>
      customerBackendApi?.listTipTargets(venueSessionId) ??
      Promise.reject(new Error("Backend unavailable")),
    enabled: backendEnabled && postType === "tip",
  });

  const tipTargets = tipTargetsQuery.data ?? [];

  const validationResult = validateScreenPost(postType, {
    message,
    igHandle,
    photoKey,
    songTitle,
    tipTargetId,
    tipAmountSatang,
  });

  const handlePhotoSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !customerBackendApi) return;
    setUploading(true);
    try {
      const { key, uploadUrl } = await customerBackendApi.requestScreenUpload(venueSessionId);
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: file,
      });
      setPhotoKey(key);
    } catch (error) {
      toast(
        error instanceof Error
          ? t("screen.uploadFailedWith", { error: error.message })
          : t("screen.uploadFailed"),
      );
    } finally {
      setUploading(false);
    }
  };

  // Generate a fresh idempotency key per submit attempt (reused on retry via ref)
  const idempotencyKeyRef = useRef<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!validationResult.ok || !customerBackendApi) return;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    setSubmitting(true);
    try {
      const post = await customerBackendApi.createScreenPost(
        {
          venueSessionId,
          postType,
          message: message || null,
          igHandle: igHandle || null,
          photoKey,
          songTitle: songTitle || null,
          tipTargetId: tipTargetId || null,
          tipAmountSatang: postType === "tip" ? tipAmountSatang : null,
          showAvatar: postType === "warp" ? true : showAvatar,
          showTable,
        },
        idempotencyKeyRef.current,
      );
      // Reset idempotency key so a second post gets a fresh one
      idempotencyKeyRef.current = null;
      onSuccess(post);
    } catch (error) {
      // Reset key on error so a retry generates a fresh attempt key (idempotent
      // per-attempt retries are handled by regenerating on next submit press)
      idempotencyKeyRef.current = null;
      toast(error instanceof Error ? error.message : t("screen.postFailed"));
    } finally {
      setSubmitting(false);
    }
  }, [
    validationResult,
    venueSessionId,
    postType,
    message,
    igHandle,
    photoKey,
    songTitle,
    tipTargetId,
    tipAmountSatang,
    showAvatar,
    showTable,
    onSuccess,
    toast,
    t,
  ]);

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={onBack}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="jod-kicker">{postTypeLabel(postType)}</div>
      <h1 className="h-display mt-1 text-[1.7rem]">{t("screen.titleBig")}</h1>

      <div className="mt-5 space-y-4">
        {/* message type — text box */}
        {postType === "message" && (
          <FormField label={t("screen.messageLabel")}>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={120}
              rows={3}
              placeholder={t("screen.messagePlaceholder")}
              className="w-full resize-none rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] px-4 py-3 text-cream text-sm placeholder:text-cream-3 focus:outline-none focus:ring-1 focus:ring-gold"
            />
            <CharCount value={message} max={120} />
          </FormField>
        )}

        {/* warp — IG handle + optional photo + optional caption */}
        {postType === "warp" && (
          <>
            <FormField label={t("screen.igLabel")}>
              <input
                type="text"
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value)}
                maxLength={60}
                placeholder="dj_velvet"
                className="w-full rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] px-4 py-3 text-cream text-sm placeholder:text-cream-3 focus:outline-none focus:ring-1 focus:ring-gold"
              />
            </FormField>
            <div>
              <span className="mb-1.5 block text-cream-2 text-xs font-medium">
                {t("screen.photoLabel")}
              </span>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="glass flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm"
              >
                <span className="shrink-0 text-cream-3 text-base leading-none">📷</span>
                <span className="flex-1 text-cream-2">
                  {uploading
                    ? t("screen.uploading")
                    : photoKey
                      ? t("screen.uploaded")
                      : t("screen.choosePhoto")}
                </span>
              </button>
            </div>
            <FormField label={t("screen.captionLabel")}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={120}
                rows={2}
                placeholder={t("screen.captionPlaceholder")}
                className="w-full resize-none rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] px-4 py-3 text-cream text-sm placeholder:text-cream-3 focus:outline-none focus:ring-1 focus:ring-gold"
              />
              <CharCount value={message} max={120} />
            </FormField>
          </>
        )}

        {/* song_request */}
        {postType === "song_request" && (
          <FormField label={t("screen.songLabel")}>
            <input
              type="text"
              value={songTitle}
              onChange={(e) => setSongTitle(e.target.value)}
              maxLength={120}
              placeholder={t("screen.songPlaceholder")}
              className="w-full rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] px-4 py-3 text-cream text-sm placeholder:text-cream-3 focus:outline-none focus:ring-1 focus:ring-gold"
            />
            <CharCount value={songTitle} max={120} />
          </FormField>
        )}

        {/* tip */}
        {postType === "tip" && (
          <>
            <FormField label={t("screen.tipTargetLabel")}>
              {tipTargetsQuery.isLoading ? (
                <p className="text-cream-3 text-xs">{t("screen.loadingShort")}</p>
              ) : tipTargets.length === 0 ? (
                <p className="text-cream-3 text-xs">{t("screen.noTipTargets")}</p>
              ) : (
                <div className="space-y-2">
                  {tipTargets
                    .filter((t) => t.active)
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((target) => {
                      const on = tipTargetId === target.id;
                      return (
                        <button
                          key={target.id}
                          type="button"
                          onClick={() => setTipTargetId(target.id)}
                          aria-pressed={on}
                          className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all ${
                            on
                              ? "border-[oklch(79%_0.12_80_/_50%)] bg-[oklch(79%_0.12_80_/_12%)]"
                              : "border-hairline bg-[oklch(100%_0_0_/_3%)]"
                          }`}
                        >
                          <span className={`flex-1 text-sm ${on ? "text-gold" : "text-cream-2"}`}>
                            {target.label}
                          </span>
                          {on && <Icons.IconCheck size={16} className="text-lime" />}
                        </button>
                      );
                    })}
                </div>
              )}
            </FormField>
            <FormField
              label={t("screen.tipAmountLabel", { min: formatMoney(DEFAULT_MIN_TIP_SATANG) })}
            >
              <div className="flex items-center gap-2">
                <span className="text-gold text-sm">฿</span>
                <input
                  type="number"
                  min={DEFAULT_MIN_TIP_SATANG / 100}
                  step={50}
                  value={tipAmountSatang / 100}
                  onChange={(e) =>
                    setTipAmountSatang(Math.max(0, Math.round(Number(e.target.value) * 100)))
                  }
                  className="w-full rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] px-4 py-3 text-cream text-sm focus:outline-none focus:ring-1 focus:ring-gold"
                />
              </div>
            </FormField>
          </>
        )}

        {/* PDPA / Identity consent section */}
        <ConsentSection
          postType={postType}
          showAvatar={showAvatar}
          showTable={showTable}
          onShowAvatarChange={setShowAvatar}
          onShowTableChange={setShowTable}
        />

        {/* Validation error */}
        {!validationResult.ok && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-red-100 text-xs">
            {validationResult.reason}
          </p>
        )}

        {!backendEnabled && (
          <p className="rounded-lg border border-hairline bg-glass px-3 py-2 text-cream-3 text-xs">
            {t("screen.mockNote")}
          </p>
        )}

        <Button
          size="lg"
          variant="accent"
          className="w-full"
          disabled={!validationResult.ok || submitting || !backendEnabled}
          onClick={() => void handleSubmit()}
        >
          {submitting ? t("screen.submitting") : t("screen.submit")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success view
// ---------------------------------------------------------------------------

interface SuccessViewProps {
  post: ScreenPost;
  onBack: () => void;
  onHome: () => void;
  venueSessionId: string;
}

function SuccessView({ post, onBack, onHome, venueSessionId }: SuccessViewProps) {
  const t = useT();
  const amountDisplay = post.amountSatang != null ? formatMoney(post.amountSatang) : null;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-5 px-[18px] py-8">
      <span className="text-6xl">🎉</span>
      <h1 className="h-display text-center text-[1.7rem]">{t("screen.successTitle")}</h1>
      <div className="glass w-full rounded-xl p-5 space-y-2 text-sm">
        <p className="text-cream text-center leading-relaxed">
          {t("screen.successBody")}
          {amountDisplay ? ` · ${amountDisplay}` : ""}
        </p>
        <p className="text-center text-cream-3 text-xs">
          {t("screen.typeLine", { type: postTypeLabel(post.postType as ScreenPostType) })}
        </p>
      </div>
      <div className="flex w-full flex-col gap-2.5">
        <Button className="w-full" onClick={onBack}>
          {t("screen.postAgain")}
        </Button>
        <Button variant="ghost" className="w-full" onClick={onHome}>
          {t("common.backHome")}
        </Button>
      </div>
      <MyPostsList venueSessionId={venueSessionId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Posts list — polls every 5 s while visible
// ---------------------------------------------------------------------------

interface MyPostsListProps {
  venueSessionId: string;
}

function MyPostsList({ venueSessionId: _venueSessionId }: MyPostsListProps) {
  const t = useT();
  const backendEnabled = customerBackendApi !== null;

  const postsQuery = useQuery({
    queryKey: ["screen", "my-posts"],
    queryFn: () =>
      customerBackendApi?.listScreenPosts() ?? Promise.reject(new Error("Backend unavailable")),
    enabled: backendEnabled,
    refetchInterval: 5_000,
  });

  const posts = postsQuery.data ?? [];

  if (!backendEnabled || (posts.length === 0 && !postsQuery.isFetching)) return null;

  return (
    <div className="mt-8 w-full">
      <h2 className="jod-kicker mb-3">{t("screen.myPosts")}</h2>
      {postsQuery.isLoading ? (
        <p className="text-cream-3 text-xs">{t("screen.loadingShort")}</p>
      ) : (
        <div className="space-y-2.5">
          {posts.map((post) => (
            <PostStatusRow key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
}

function PostStatusRow({ post }: { post: ScreenPost }) {
  const meta = screenPostStatusMeta(post.status as ScreenPostStatus);
  const toneClass =
    meta.tone === "lime"
      ? "text-lime"
      : meta.tone === "gold"
        ? "text-gold"
        : meta.tone === "red"
          ? "text-red-300"
          : "text-cream-3";

  const preview =
    post.message ??
    post.songTitle ??
    (post.igHandle ? `@${post.igHandle}` : null) ??
    postTypeLabel(post.postType as ScreenPostType);

  return (
    <div className="glass flex items-center gap-3 rounded-lg px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-cream-2 text-sm">{preview}</div>
        <div className="text-cream-3 text-[0.68rem]">
          {postTypeLabel(post.postType as ScreenPostType)}
          {post.rejectReason ? ` · ${post.rejectReason}` : ""}
        </div>
      </div>
      <span className={`shrink-0 font-medium text-[0.75rem] ${toneClass}`}>{meta.label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="mb-1.5 block font-medium text-cream-2 text-xs">{label}</span>
      {children}
    </div>
  );
}

function CharCount({ value, max }: { value: string; max: number }) {
  const remaining = max - value.length;
  return (
    <div className="mt-1 text-right text-[0.65rem] text-cream-3">
      {remaining} / {max}
    </div>
  );
}

interface ConsentSectionProps {
  postType: ScreenPostType;
  showAvatar: boolean;
  showTable: boolean;
  onShowAvatarChange: (v: boolean) => void;
  onShowTableChange: (v: boolean) => void;
}

function ConsentSection({
  postType,
  showAvatar,
  showTable,
  onShowAvatarChange,
  onShowTableChange,
}: ConsentSectionProps) {
  const t = useT();
  // For warp: showAvatar is always ON (implied by the feature)
  const avatarLocked = postType === "warp";
  const effectiveShowAvatar = avatarLocked ? true : showAvatar;

  return (
    <div className="rounded-xl border border-hairline bg-[oklch(100%_0_0_/_3%)] px-4 py-4 space-y-3">
      <div className="flex items-start gap-2">
        <Icons.IconEye size={15} className="mt-0.5 shrink-0 text-gold" />
        <p className="text-cream-3 text-[0.72rem] leading-relaxed">{t("screen.pdpaNote")}</p>
      </div>

      <Toggle
        label={t("screen.showAvatar")}
        checked={effectiveShowAvatar}
        disabled={avatarLocked}
        onChange={onShowAvatarChange}
        note={avatarLocked ? t("screen.avatarLockedNote") : undefined}
      />
      <Toggle label={t("screen.showTable")} checked={showTable} onChange={onShowTableChange} />
    </div>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  note?: string;
}

function Toggle({ label, checked, disabled = false, onChange, note }: ToggleProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="flex w-full items-center gap-3 text-left disabled:opacity-60"
    >
      <span className="flex-1">
        <span className="text-cream-2 text-sm">{label}</span>
        {note && <span className="ml-1 text-cream-3 text-[0.68rem]">({note})</span>}
      </span>
      <span
        className={`flex h-6 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          checked ? "bg-lime" : "bg-[oklch(100%_0_0_/_12%)]"
        }`}
      >
        <span
          className="size-5 rounded-full bg-cream transition-transform"
          style={{ transform: checked ? "translateX(1rem)" : "translateX(0)" }}
        />
      </span>
    </button>
  );
}
